/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
*/

// Core imports
import React, { useEffect, useState, Fragment } from 'react';
import { useTranslation } from 'react-i18next';

// Component imports
import { Button, Row, Space, Modal, Typography, Upload, Form, Select, Col } from 'antd';

// Misc imports
import { useCreateKnowledgeBaseArticlesFromFileMutation, useCreateKnowledgeBaseMutation, useFetchKnowledgeBaseQuery } from '../../../store/knowledgeBases/knowledgeBasesApi';
import { KNOWLEDGE_BASE_UPLOAD_STATUSES } from '../../../constants/knowledgeBases';
import { KNOWN_TOPICS_KEYS } from '../../../components/HRWiz/HRWizArticles';
import { useBotLanguage } from '../useBotLanguage';
import moment from 'moment';

// Constant declarations
const { Dragger } = Upload;
const { Text } = Typography;

/**
 * @typedef {Object} UploadKnowledgeBaseFileHook
 * @property {string} UploadKnowledgeBaseFileHook.status Current status. Options are: `idle`, `processing`, `uploading`.
 * @property {string} UploadKnowledgeBaseFileHook.createdKnowledgeBaseId The id of the Knowledge Base created. Only available when `autoCreateKnowledgeBase` is `true`.
 * @property {Function} UploadKnowledgeBaseFileHook.renderUploadFileButton Renders a button to upload a file. If a button component is passed as an argument, it will be rendered instead of the default button.
 * @property {Function} UploadKnowledgeBaseFileHook.showModalForFileUpload Shows a modal to upload a file to a knowledge base.
 */

/**
 * Provides a hook to upload a file to a knowledge base and poll for the status of the upload.
 *
 * @example
 * const { status, renderUploadFileButton, showModalForFileUpload } = useUploadKnowledgeBaseFile({
 *   orgId: 'orgId',
 *   knowledgeBaseId: 'knowledgeBaseId'
 * });
 *
 * @param {Object} options
 * @param {string} options.orgId organization id of the knowledge base
 * @param {string} options.knowledgeBaseId knowledge base id to upload the file to
 * @param {string} options.autoCreateKnowledgeBase when `true` it will automatically create a knowledge base if it doesn't exist (knowledgeBaseId is ignored)
 * @param {Object} options.newKnowledgeBaseData data to create a new knowledge base
 * @param {Object} options.language the knowledge base language
 * @param {Boolean} options.showSuccessMessages whether to show success messages or not (default is `true`)
 * @param {Object} options.onFileUploaded callback to be called when the file is uploaded
 * @returns {UploadKnowledgeBaseFileHook}
 * @returns
 */
const useUploadKnowledgeBaseFile = ({ orgId, knowledgeBaseId, autoCreateKnowledgeBase, newKnowledgeBaseData, language, showSuccessMessages = true, onFileUploaded } = {}) => {
  const { t } = useTranslation();

  const [pollKnowledgeBase, setPollKnowledgeBase] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [extraTags, setExtraTags] = useState([]);
  const [forceProcessingState, setForceProcessingState] = useState(false); // Temporary state to force processing state before polling and after upload
  const [createKnowledgeBase] = useCreateKnowledgeBaseMutation();
  const { primaryBotLanguage } = useBotLanguage();
  const [createdKnowledgeBaseId, setCreatedKnowledgeBaseId] = useState(null);
  const [form] = Form.useForm();
  const kb = useFetchKnowledgeBaseQuery(
    { orgId, knowledgeBaseId },
    {
      pollingInterval: pollKnowledgeBase ? 5000 : 0, // 0 disables polling
      skip: !orgId || !knowledgeBaseId
    }
  );

  const [
    uploadFileMutation,
    { isLoading: isUploading }
  ] = useCreateKnowledgeBaseArticlesFromFileMutation();

  useEffect(() => {
    if (kb.data?.status?.toLowerCase() === KNOWLEDGE_BASE_UPLOAD_STATUSES.PROCESSING) {
      setPollKnowledgeBase(true);
    } else {
      setPollKnowledgeBase(false);
      // Reset forceProcessingState after polling
      setForceProcessingState(false);
    }
  }, [kb.data?.status]);

  const showModalForFileUpload = () => {
    setIsModalVisible(true);
  };

  const handleUpload = async () => {
    let _knowledgeBaseId = knowledgeBaseId;
    const file = fileList[0];
    if (!_knowledgeBaseId && autoCreateKnowledgeBase) {
      const valuesToSubmit = {
        primaryLanguage: language || primaryBotLanguage,
        languages: [
          { languageCode: language || primaryBotLanguage, isPrimary: true }
        ],
        spaces: ['ex'],
        name: `${file?.name || 'New source'} - ${moment().format('YYYYMMDDhhmm')}`,
        ...newKnowledgeBaseData
      };
      const newKB = await createKnowledgeBase({ orgId, data: valuesToSubmit, successMessage: { showMessage: showSuccessMessages } }).unwrap();
      _knowledgeBaseId = newKB.id;
      setCreatedKnowledgeBaseId(newKB.id);
    }

    const formData = new FormData();
    formData.append('file', file);
    const tags = Object.values(KNOWN_TOPICS_KEYS);
    const finalTagsSet = new Set([...tags, ...extraTags]);
    const finalTagsArray = Array.from(finalTagsSet);
    formData.append('classificationTags', JSON.stringify(finalTagsArray));

    setIsModalVisible(false);
    try {
      const fileUploaded = await uploadFileMutation({
        orgId,
        knowledgeBaseId: _knowledgeBaseId,
        data: formData,
        successMessage: { showMessage: showSuccessMessages }
      }).unwrap();

      if (fileUploaded) {
        form.resetFields();
        setPollKnowledgeBase(true);
        setForceProcessingState(true);
        setFileList([]);
        setExtraTags([]);
        if (onFileUploaded) {
          onFileUploaded(fileUploaded);
        }
      }
    } catch (error) { /* Do nothing */ }
  };

  const handleRemove = (_file) => {
    setFileList([]);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setFileList([]);
    setExtraTags([]);
    form.resetFields();
  };

  const renderUploadButton = () => {
    return (
      <>
        <Row gutter={64} align="middle" justify="center" className="mt-5 mb-5">
          <Space size={32}>
            <Button size="large">{t('Select')}</Button>
            <Text>{t('or')}</Text>
            <Text strong>{t('Drop File to this area')}</Text>
          </Space>
        </Row>

        <Text type="secondary">
          {t('Support for a single upload. Accepted file types: .pdf, .docx, .txt, .md, .pptx, .html. Maximum upload file size: 50MB')}
        </Text>
      </>
    );
  };

  const renderUI = (ButtonComp) => {
    const uploadProps = {
      name: 'file',
      accept: 'application/pdf, text/*, application/vnd.openxmlformats-officedocument.wordprocessingml.document , application/msword, .md, .pptx',
      fileList: fileList,
      openFileDialogOnClick: true,
      onRemove: handleRemove,
      showUploadList: { showRemoveIcon: true, showPreviewIcon: false },
      beforeUpload: (file) => {
        setFileList([file]);
        return false;
      }
    };

    return (
      <div>
        {
          ButtonComp ? (
            ButtonComp
          ) : (
            <div onClick={showModalForFileUpload}>{t('Upload file')}</div>
          )
        }

        <Modal
          title={t('Upload file')}
          open={isModalVisible}
          onCancel={handleCancel}
          onOk={handleUpload}
          okButtonProps={{ disabled: fileList.length === 0 }}
        >
          <Fragment>
            <Dragger {...uploadProps}>{renderUploadButton()}</Dragger>

            <Form
              className="mt-5"
              layout="vertical"
              form={form}
            >
              <Row>
                <Col span={24}>
                  <Form.Item
                    name="useTags"
                    label={t('Identify these additional topics in the uploaded file')}
                    labelAlign="left"
                  >
                    <Select
                      mode="tags"
                      size="large"
                      tokenSeparators={[',']}
                      onChange={(value) => setExtraTags(value)}
                      placeholder={t('Add topics separated by commas')}
                      onInputKeyDown={event => {
                        // Prevent removing the last tag when pressing enter
                        if (event.key === 'Enter' && !event.target.value) {
                          return event.stopPropagation();
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Fragment>
        </Modal>
      </div>
    );
  };

  return {
    status: isUploading
      ? KNOWLEDGE_BASE_UPLOAD_STATUSES.UPLOADING
      : forceProcessingState
        ? KNOWLEDGE_BASE_UPLOAD_STATUSES.PROCESSING
        : kb.data?.status?.toLowerCase(),
    createdKnowledgeBaseId,
    renderUploadFileButton: renderUI,
    showModalForFileUpload
  };
};

export default useUploadKnowledgeBaseFile;
